import React from 'react';
import HeroPlaceholder from '../components/HeroPlaceholder';
import FaqAccordion from '../components/FaqAccordion';
import CtaBand from '../components/CtaBand';
import SEO from '../components/SEO';
import AeoCallout from '../components/AeoCallout';
import RelatedBand from '../components/RelatedBand';
import MattingCalculator from '../components/MattingCalculator';
import { HERO_MEDIA } from '../config/media';
import { usePageContent } from '../lib/useContent';
import {
    orgJsonLd,
    breadcrumbJsonLd,
    faqJsonLd,
} from '../lib/seo';

// Frozen inline fallbacks matching current page JSX verbatim.
const OG_FALLBACK = {
    hero: {
        eyebrow: 'Oil & Gas / Pipeline',
        headline: 'Engineered for pipeline corridors and well-pad access in Western Canada.',
        body: 'Pipeline construction across Alberta, BC, and Saskatchewan demands ground protection that survives muskeg crossings, frozen winter haul roads, and environmentally sensitive corridors. Centurion delivers ground protection from upstream well-pad access through mainline construction to tank-farm staging - Manufacturer Direct.',
        image_url: HERO_MEDIA.pipelineConstruction.src,
    },
    applications: {
        items: [
            {
                title: 'Pipeline mainline construction',
                body: 'Continuous Centurion access mats along the right-of-way. Three-ply interlocking timber assemblies prevent compaction across muskeg, wetland, and frozen ground. Transition / ramp mats between mat corridors and gravel roads.',
            },
            {
                title: 'Well pad access roads',
                body: 'Standard 8\u2032\u00D714\u2032\u00D75\u2033 Traditional Access Mats - 477 mats per kilometre on straight corridor, plus 25 mats per corner. Re-deployable across multiple wells across a campaign.',
            },
            {
                title: 'Tank farm + staging',
                body: 'Access matting for staging yards and lay-down areas. Crane mats for tank set, stairs and module placement. Built to your geotech and rigging plan.',
            },
        ],
    },
    faq: {
        items: [
            {
                q: 'How many access mats per kilometre of pipeline?',
                a: 'A Centurion access road runs roughly 477 mats per kilometre on a straight corridor, plus an additional 25 mats per corner or turn. Use the Field Calculator on this page for an exact project-specific estimate.',
            },
            {
                q: 'Can Centurion access mats handle muskeg crossings?',
                a: 'Yes - that\u2019s the design case. Muskeg cannot carry concentrated loads, so the mat assembly spreads tracked-equipment and truck loads across many mats via 12\u2033 interlocking pockets and the 177-bolt reinforced pattern. For chronic muskeg corridors, doubled-up mat sections or transitional ramp mats are common.',
            },
            {
                q: 'What is the difference between access mats and rig mats for oil & gas?',
                a: 'Access mats spread the rolling/tracked load of equipment moving across the corridor; rig mats provide a stiffer, heavier working deck for stationary drilling rigs, workover rigs, and frac spreads to sit on for weeks at a time. Rig mats are typically thicker and longer (up to 40 ft).',
            },
            {
                q: 'How fast can Centurion deliver to a Western Canadian pipeline site?',
                a: 'Manufacturer Direct from our Chemainus, BC mill, typical lead time matches the project delivery milestone. Highway haul to BC, AB, SK, MB, YT, NT and beyond - full-load dispatch coordinated through Centurion logistics.',
            },
            {
                q: 'Does Centurion work with First Nations groups on pipeline projects?',
                a: 'Yes. Through our partner Bear Valley Solutions - an Indigenous First Nations economic development organization - Centurion supports employment, procurement and joint-venture frameworks with Indigenous First Nations across Western Canada. Engagement is project-by-project and led by community priorities and existing partnership agreements.',
            },
        ],
    },
};

export default function OilGasPipeline() {
    const content = usePageContent('projects.oil-gas', OG_FALLBACK);
    const hero = content.hero || OG_FALLBACK.hero;
    const applications = (content.applications?.items?.length ? content.applications.items : OG_FALLBACK.applications.items);
    const faqs = (content.faq?.items?.length ? content.faq.items : OG_FALLBACK.faq.items);

    return (
        <>
            <SEO
                title="Oil & Gas / Pipeline Matting - Access & Rig Mats BC AB SK"
                description="Centurion access and rig mats for oil & gas pipeline corridors, well pads, tank farms, and frac spreads in BC, Alberta, and Saskatchewan. 477 mats/km Traditional Access Mat - Manufacturer Direct."
                cmsSlug="seo.projects.oil-gas"
                path="/projects/oil-gas-pipeline"
                image={HERO_MEDIA.pipelineConstruction.src}
                jsonLd={[
                    orgJsonLd(),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'Projects', path: '/projects' },
                        { name: 'Oil & Gas / Pipeline', path: '/projects/oil-gas-pipeline' },
                    ]),
                    faqJsonLd(faqs),
                    {
                        '@context': 'https://schema.org',
                        '@type': 'Service',
                        serviceType: 'Oil & Gas Pipeline Matting Supply',
                        provider: { '@type': 'Organization', name: 'Centurion Matting Products' },
                        areaServed: 'Western Canada',
                        url: 'https://centurionmatting.com/projects/oil-gas-pipeline',
                    },
                ]}
            />

            <HeroPlaceholder
                mediaKey="pipelineConstruction"
                imageUrl={hero.image_url}
                eyebrow={hero.eyebrow}
                title={hero.headline}
                subtitle={hero.body}
            />

            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">Overview</span>
                <p className="mt-8 font-sans text-lg leading-relaxed text-brand-text">
                    Centurion access mats - 8&prime;&times;14&prime;&times;5&Prime;, coastal Hem-Fir Hi-Line #2 - deliver consistent load distribution across muskeg crossings, frozen haul roads, and environmentally sensitive corridors. Every mat is milled Manufacturer Direct from our Chemainus, BC mill, spec&rsquo;d to prevent soil compaction and to protect the subsurface utilities that pipelines must coexist with.
                </p>
                <p className="mt-6 font-sans text-base leading-relaxed text-brand-muted">
                    From upstream well-pad access to mainline construction and tank farm staging, Centurion supplies owners, contractors, and First Nations groups with quality and custom-built matting solutions delivered on schedule and on budget.
                </p>
            </section>

            {/* Application table */}
            <section className="border-y border-brand-hairline bg-brand-surface" data-eyebrow-tone="blue">
                <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
                    <span className="eyebrow">Where Centurion Mats Work</span>
                    <h2 className="h-display mt-6 max-w-3xl text-3xl sm:text-4xl">
                        Three pipeline applications, one engineered system.
                    </h2>
                    <div className="mt-12 grid gap-px bg-brand-hairline md:grid-cols-3" data-testid="og-applications">
                        {applications.map((a, i) => (
                            <div key={a.title} className="bg-brand-surface p-6 md:p-8">
                                <span className="font-display text-xs font-bold tabular-nums tracking-[0.2em] text-brand-gold">
                                    0{i + 1}
                                </span>
                                <h3 className="mt-5 font-display text-lg font-bold uppercase tracking-tight text-brand-text">
                                    {a.title}
                                </h3>
                                <p className="mt-3 font-sans text-sm leading-relaxed text-brand-muted">
                                    {a.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Spec callout + Hem-Fir paragraph */}
            <section className="mx-auto max-w-7xl grid gap-12 px-6 py-20 md:grid-cols-[1.1fr_1fr] md:gap-16 md:px-10 md:py-28">
                <div>
                    <span className="eyebrow">Why Coastal Hem-Fir</span>
                    <h2 className="h-display mt-6 text-2xl sm:text-3xl">
                        Density. Strength. Longevity.
                    </h2>
                    <p className="mt-5 font-sans text-base leading-relaxed text-brand-muted">
                        Coastal Hem-Fir from BC’s wet coast forests has the tight, consistent grain pattern that gives Centurion mats their multi-deployment lifespan. We’ve specified Hi-Line #2 structural grade for three generations - in muskeg, on frozen winter haul, and across mat corridors that get re-laid project after project.
                    </p>
                </div>
                <AeoCallout
                    testId="aeo-og-spec"
                    q="What size access mat does Centurion supply for pipeline work?"
                    a={'Standard 8\u2032\u00D714\u2032\u00D75\u2033 Traditional Access Mat, coastal Hem-Fir Hi-Line #2, 10\u2033 interlocking fingers, 177-bolt reinforced pattern. Custom sizes available on request from our Chemainus, BC mill.'}
                />
            </section>

            {/* Calculator embed */}
            <section className="border-y border-brand-hairline bg-brand-surface" id="calculator">
                <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
                    <span className="eyebrow">Field Calculator</span>
                    <h2 className="h-display mt-6 max-w-3xl text-3xl sm:text-4xl">
                        Run your pipeline numbers in 30 seconds.
                    </h2>
                    <p className="mt-5 max-w-2xl font-sans text-base text-brand-muted">
                        Distance plus corners, or pad dimensions. The math is the same one our yard runs.
                    </p>
                    <div className="mt-10">
                        <MattingCalculator variant="compact" />
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">FAQ</span>
                <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                    Common questions about pipeline matting.
                </h2>
                <div className="mt-10">
                    <FaqAccordion faqs={faqs} testId="faq-og" />
                </div>
            </section>

            <RelatedBand
                heading="Adjacent project hubs."
                items={[
                    { to: '/projects', eyebrow: 'Portfolio', title: 'All Projects', body: 'Pipeline, infrastructure, wind, and partnerships in one view.' },
                    { to: '/projects/infrastructure', eyebrow: 'Infrastructure', title: 'Infrastructure Projects', body: 'Power & transmission, civil, utility - engineered ground protection.' },
                    { to: '/projects/renewables', eyebrow: 'Renewables', title: 'Wind & Renewables', body: 'Crane pads and access roads for wind farm and solar construction.' },
                ]}
            />

            <CtaBand
                eyebrow="Manufacturer Direct"
                title="Spec the matting package for your next pipeline project."
                body="Pipeline mainline, well pads, tank farms - Manufacturer Direct from our Chemainus, BC mill."
                cta={{ to: '/quote?mat_type=access&project_type=pipeline', label: 'Get a Manufacturer Direct Quote' }}
                secondary={{ to: '/contact', label: 'Talk to Sales' }}
            />
        </>
    );
}
